<template>
  <section class="card">
    <h2 class="section-heading">
      {{ t('employerForm.candidate.title') }}
    </h2>

    <div class="form-grid">
      <div class="form_group">
        <label>{{ t('employerForm.candidate.firstName') }}</label>

        <input type="text" v-model="form.firstName" :placeholder="t('employerForm.candidate.firstName')"
          @input="onNameInput('firstName')" :class="{ 'is-invalid': formErrors.has('candidate.firstName') }" />

        <p class="invalid-feedback" v-if="formErrors.has('candidate.firstName')">
          {{ t(formErrors.first('candidate.firstName')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.middleName') }}</label>

        <input type="text" v-model="form.middleName" :placeholder="t('employerForm.candidate.middleName')" @input="onNameInput('middleName')"/>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.lastName') }}</label>

        <input type="text" v-model="form.lastName" :placeholder="t('employerForm.candidate.lastName')"
          @input="onNameInput('lastName')" :class="{ 'is-invalid': formErrors.has('candidate.lastName') }" />

        <p class="invalid-feedback" v-if="formErrors.has('candidate.lastName')">
          {{ t(formErrors.first('candidate.lastName')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.passportNumber') }}</label>

        <input type="text" v-model="form.passport" :placeholder="t('employerForm.candidate.passportNumber')"
          @input="clearError('candidate.passport')"
          :class="{ 'is-invalid': formErrors.has('candidate.passport') }" />

        <p class="invalid-feedback" v-if="formErrors.has('candidate.passport')">
          {{ t(formErrors.first('candidate.passport')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.gender') }}</label>

        <select v-model="form.gender" @change="clearError('candidate.gender')"
          :class="{ 'is-invalid': formErrors.has('candidate.gender') }">
          <option value="">
            {{ t('common.please_select') }}
          </option>
          <option value="male">
            {{ t('gender.male') }}
          </option>

          <option value="female">
            {{ t('gender.female') }}
          </option>
        </select>

        <p class="invalid-feedback" v-if="formErrors.has('candidate.gender')">
          {{ t(formErrors.first('candidate.gender')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.birthDate') }}</label>

        <input type="date" 
          :max="minBirthDate"
          :min="maxBirthDate"
          v-model="form.dob"
          @input="clearError('candidate.dob')"
          :class="{ 'is-invalid': formErrors.has('candidate.dob') }" />

        <p class="invalid-feedback" v-if="formErrors.has('candidate.dob')">
          {{ t(formErrors.first('candidate.dob')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.countryOfOrigin') }}</label>
          <select
            v-model="form.countryOfOrigin"
            :class="{ 'is-invalid': formErrors.has('candidate.countryOfOrigin') }"
          >
            <option value="" disabled>
              {{ t('employerForm.candidate.countryOfOrigin') }}
            </option>
            <option
              v-for="country in countryOptions"
              :key="country.code"
              :value="country.code"
            >
              {{ country.name }}
            </option>
          </select>
        <p class="invalid-feedback" v-if="formErrors.has('candidate.countryOfOrigin')">
          {{ t(formErrors.first('candidate.countryOfOrigin')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.phone') }}</label>

        <input 
          type="tel"
          inputmode="numeric"
          v-model="form.phone" 
          :placeholder="t('employerForm.candidate.phone')"
          :class="{ 'is-invalid': formErrors.has('candidate.phone') }"
          @input="onPhoneInput"
          @keypress="allowPhoneCharacters"
           />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.phone')">
          {{ t(formErrors.first('candidate.phone')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.entryToIsraelDate') }}</label>
        <input 
          type="date"
          v-model="form.entryToIsraelDate"
          :class="{ 'is-invalid': formErrors.has('candidate.entryToIsraelDate') }" 
          @input="clearError('candidate.entryToIsraelDate')"
          />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.entryToIsraelDate')">
          {{ t(formErrors.first('candidate.entryToIsraelDate')) }}
        </p>
      </div>
    </div>

    <h3 class="sub-heading">
      {{ t('employerForm.candidate.addressSection') }}
    </h3>

    <div class="form-grid">
      <div class="form_group">
        <label>{{ t('employerForm.candidate.street') }}</label>

        <input type="text" @input="clearError('candidate.address.street')" v-model="form.address.street"
          :class="{ 'is-invalid': formErrors.has('candidate.address.street') }" />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.address.street')">
          {{ t(formErrors.first('candidate.address.street')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.houseNumber') }}</label>

        <input type="text" @input="clearError('candidate.address.houseNumber')" v-model="form.address.houseNumber"
          :class="{ 'is-invalid': formErrors.has('candidate.address.houseNumber') }" />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.address.houseNumber')">
          {{ t(formErrors.first('candidate.address.houseNumber')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.apartmentNumber') }}</label>

        <input type="text" @input="clearError('candidate.address.apartmentNumber')" v-model="form.address.apartmentNumber"
          :class="{ 'is-invalid': formErrors.has('candidate.address.apartmentNumber') }" />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.address.apartmentNumber')">
          {{ t(formErrors.first('candidate.address.apartmentNumber')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.city') }}</label>

        <input type="text" @input="clearError('candidate.address.city')" v-model="form.address.city"
          :class="{ 'is-invalid': formErrors.has('candidate.address.city') }" />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.address.city')">
          {{ t(formErrors.first('candidate.address.city')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.zipCode') }}</label>

        <input type="text" @input="clearError('candidate.address.zipCode')" v-model="form.address.zipCode"
          :class="{ 'is-invalid': formErrors.has('candidate.address.zipCode') }" />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.address.zipCode')">
          {{ t(formErrors.first('candidate.address.zipCode')) }}
        </p>
      </div>
    </div>
    <h3 class="sub-heading">
      {{ t('employerForm.candidate.insurancePeriodSection') }}
    </h3>

    <div class="form-grid">
      <div class="form_group">
        <label>{{ t('employerForm.candidate.insuranceFrom') }}</label>

        <input
          type="date"
          :min="formattedToday"
          v-model="form.insurancePeriod.from"
          :class="{ 'is-invalid': formErrors.has('candidate.insurancePeriod.from') }" 
          @input="onFromInput"
          />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.insurancePeriod.from')">
          {{ t(formErrors.first('candidate.insurancePeriod.from')) }}
        </p>
      </div>

      <div class="form_group">
        <label>{{ t('employerForm.candidate.insuranceTo') }}</label>

        <input
          type="date" 
          v-model="form.insurancePeriod.to"
          :max="expiryMax"
          :min="form.insurancePeriod.from"
          :class="{ 'is-invalid': formErrors.has('candidate.insurancePeriod.to') }" 
          @input="onToInput"
        />
        <p class="invalid-feedback" v-if="formErrors.has('candidate.insurancePeriod.to')">
          {{ t(formErrors.first('candidate.insurancePeriod.to')) }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import Error from '@/utils/Error';
import { allowPhoneCharacters, sanitizePhoneNumber } from '@/utils/helpers';
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },

  formErrors: {
    type: Object,
    required: false,
    default: reactive(new Error({}))
  },
  countries: {
    type: [Array, Object],
    default: () => []
  }
});
const today = new Date();
const formattedToday = new Date().toISOString().split('T')[0];

const minBirthDate = new Date(
  today.getFullYear()-18, today.getMonth(), today.getDate()
).toISOString().split('T')[0];

const maxBirthDate = new Date(
  today.getFullYear()-65,
  today.getMonth(),
  today.getDate()+1
).toISOString().split('T')[0];

const emit = defineEmits([
  'update:modelValue',
  'removeError'
]);

const form = reactive({
  address: {},
  insurancePeriod: {},
  ...props.modelValue
});
const expiryMax = computed(() => {
  const fromValue = form.insurancePeriod.from;
  const isValidFormat = fromValue && /^\d{4}-\d{2}-\d{2}$/.test(fromValue);

  const current = isValidFormat ? new Date(fromValue) : new Date(formattedToday);
  if (isNaN(current.getTime())) {
    return null;
  }
  const expiry = new Date(
    current.getTime()+ ((365 - 1) * 24 * 60 * 60 * 1000)
  );
  return expiry.toISOString().split('T')[0];
});
const countryOptions = computed(() =>
  Object.entries(props.countries).map(([code, name]) => ({ code, name }))
);

watch(
  form,
  (val) => emit('update:modelValue', val),
  { deep: true }
);

watch(
  () => props.modelValue,
  (newValue) => {
    Object.assign(form, {
      address: {},
      insurancePeriod: {},
      ...newValue
    });
  },
  { deep: true, immediate: true }
);

watch(
  expiryMax,
  (newExpiry) => {
    if (
      !form.insurancePeriod.to ||
      form.insurancePeriod.to > newExpiry
    ) {
      form.insurancePeriod.to = newExpiry;
    }
  },
  { immediate: true }
);

const clearError = (name) => {
  emit('removeError', name);
};
const onPhoneInput = (event) => {
  form.phone = sanitizePhoneNumber(event.target.value);
  clearError('candidate.phone');
};

const onFromInput = () =>{
  clearError('candidate.insurancePeriod.from');
  const value = form.insurancePeriod.from;
  if (!value) return;
  const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const parsed = new Date(value);
  const isValidDate = isValidFormat && !isNaN(parsed.getTime());

  if (!isValidDate || value < formattedToday) {
    form.insurancePeriod.from = formattedToday;
  }
}
const onToInput = () => {
  clearError('candidate.insurancePeriod.to');

  const value = form.insurancePeriod.to;
  const minDate = form.insurancePeriod.from || formattedToday;
  const maxDate = expiryMax.value;

  if (!value) return;

  const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const parsed = new Date(value);
  const isValidDate = isValidFormat && !isNaN(parsed.getTime());

  if (!isValidDate) {
    form.insurancePeriod.to = maxDate ?? minDate;
    return;
  }
  if (value < minDate) {
    form.insurancePeriod.to = minDate;
  } else if (maxDate && value > maxDate) {
    form.insurancePeriod.to = maxDate;
  }
};
const onNameInput = (field) => {
  clearError(`candidate.${field}`);
  const filtered = form[field].replace(/[^a-zA-Z\s'-]/g, '');
  if (filtered !== form[field]) {
    form[field] = filtered;

  }
}
</script>

<style scoped>
.sub-heading {
  margin-top: 32px;
  margin-bottom: 16px;
  font-size: 18px;
  font-weight: 600;
}
.radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 10px;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.other-purpose-input {
  margin-top: 14px;
}
</style>